import { useEffect, useState, type FormEvent } from "react";
import { Loader2 } from "lucide-react";
import type { MailAccountSummary, MailProviderPreset } from "@/lib/types/mail";
import { fetchMailProviders, saveMailAccount, testMailConnection } from "@/lib/api/accounts";

type Props = {
  account: MailAccountSummary;
  onSuccess: () => void;
};

export function AccountSettingsPasswordForm({ account, onSuccess }: Props) {
  const [provider, setProvider] = useState<MailProviderPreset | null>(null);
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void fetchMailProviders().then((res) => {
      if (cancelled || !res.ok) return;
      const match =
        res.data.providers.find((p) => p.id === account.providerId) ??
        res.data.providers.find((p) => p.id === "manual") ??
        null;
      setProvider(match);
    });
    return () => {
      cancelled = true;
    };
  }, [account.providerId]);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!provider || !password.trim()) return;

    setBusy(true);
    setError(null);
    setSuccess(null);

    const isManual = provider.id === "manual";
    const payload = {
      providerId: provider.id,
      email: account.email,
      password,
      ...(isManual
        ? {
            imap: {
              host: account.imapHost,
              port: 993,
              encryption: "ssl" as const,
            },
            smtp: {
              host: account.smtpHost,
              port: 587,
              encryption: "starttls" as const,
            },
          }
        : {}),
    };

    const test = await testMailConnection(payload);
    if (!test.ok) {
      setError(test.message);
      setBusy(false);
      return;
    }

    const save = await saveMailAccount(payload);
    setBusy(false);

    if (!save.ok) {
      setError(save.message);
      return;
    }

    setPassword("");
    setSuccess("Mot de passe mis à jour.");
    onSuccess();
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-3">
      <p className="text-xs text-[var(--mail-muted)]">
        Saisissez le nouveau mot de passe de la boîte mail. La connexion IMAP et SMTP sera
        vérifiée avant enregistrement.
      </p>
      <label className="block space-y-1">
        <span className="text-xs font-medium text-[var(--mail-text)]">Nouveau mot de passe</span>
        <input
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          autoComplete="new-password"
          className="w-full rounded-xl border border-[var(--mail-border-soft)] bg-[var(--mail-surface)] px-3 py-2 text-sm"
          placeholder="••••••••"
        />
      </label>
      {error ? (
        <p className="text-xs text-[var(--mail-danger-text)]" role="alert">
          {error}
        </p>
      ) : null}
      {success ? <p className="text-xs text-emerald-600">{success}</p> : null}
      <button
        type="submit"
        disabled={busy || !password.trim() || !provider}
        className="mail-btn-primary inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium disabled:opacity-50"
      >
        {busy ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : null}
        Enregistrer le mot de passe
      </button>
    </form>
  );
}
